// © Microsoft Corporation. All rights reserved.

import { NavBar, NavigationItem } from "./NavBar";
import { html, ref, repeat, TemplateValue, when } from "@microsoft/fast-element";
import { endTemplate } from "@microsoft/fast-foundation";
import { telemetry } from "@fast/components";
import { MsnNavItem } from "./nav-item";

MsnNavItem;

const overflowButtonId = "overflow-menu-button";

const navItemTemplate = html<NavigationItem>`
    <li part="list-item" class="${x => x.showPipeSeparator && "pipe"}">
        <msn-nav-item
            id="${x => x.id}"
            href="${x => x.href}"
            active=${(x, c) => x.id === c.parent.activeId || null}
            ${telemetry(x => x.telemetryMetadata ? JSON.stringify(x.telemetryMetadata) : null)}
            @click="${(x, c) => c.parent.onNavItemClick(x)}"
            @keypress="${(x, c) =>
        c.parent.onNavItemKeyPress(c.event as KeyboardEvent, x)}"
        >
            ${when(x => x.glyph, html<NavigationItem>`<span slot="start">${x => html`${x.glyph as TemplateValue<NavigationItem>}`}</span>`)}
            ${x => x.display}
        </msn-nav-item>
    </li>
`;

const overflowButton = html<NavBar>`
    <fluent-button
        type="button"
        id="${overflowButtonId}"
        ${ref("overflowButton")}
        appearance="stealth"
        aria-label="${x => x.localizedStrings.overflowButtonTooltip || null}"
        title="${x => x.localizedStrings.overflowButtonTooltip || null}"
        aria-haspopup="true"
        aria-expanded="${x => (x.showMenu ? "true" : null)}"
        @click=${(x, c) => x.toggleOverflowMenu()}
        data-t="${x => x.overflowTelemetryTag}"
    >
        <svg>
            <path d="M2 7C2.14063 7 2.27083 7.02607 2.39063 7.07813C2.51041 7.1302 2.61459 7.20313 2.70313 7.29687C2.79687 7.3854 2.86979 7.4896 2.92187 7.6094C2.97396 7.7292 3 7.8594 3 8C3 8.1406 2.97396 8.2708 2.92187 8.3906C2.86979 8.5104 2.79687 8.6172 2.70313 8.71093C2.61459 8.79947 2.51041 8.8698 2.39063 8.92187C2.27083 8.97393 2.14063 9 2 9C1.85937 9 1.72917 8.97393 1.60937 8.92187C1.48959 8.8698 1.38281 8.79947 1.28906 8.71093C1.20052 8.6172 1.13021 8.5104 1.07813 8.3906C1.02604 8.2708 1 8.1406 1 8C1 7.8594 1.02604 7.7292 1.07813 7.6094C1.13021 7.4896 1.20052 7.3854 1.28906 7.29687C1.38281 7.20313 1.48959 7.1302 1.60937 7.07813C1.72917 7.02607 1.85937 7 2 7ZM8 7C8.1406 7 8.2708 7.02607 8.3906 7.07813C8.5104 7.1302 8.6146 7.20313 8.70313 7.29687C8.79687 7.3854 8.8698 7.4896 8.92187 7.6094C8.97393 7.7292 9 7.8594 9 8C9 8.1406 8.97393 8.2708 8.92187 8.3906C8.8698 8.5104 8.79687 8.6172 8.70313 8.71093C8.6146 8.79947 8.5104 8.8698 8.3906 8.92187C8.2708 8.97393 8.1406 9 8 9C7.8594 9 7.7292 8.97393 7.6094 8.92187C7.4896 8.8698 7.3828 8.79947 7.28907 8.71093C7.20053 8.6172 7.1302 8.5104 7.07813 8.3906C7.02607 8.2708 7 8.1406 7 8C7 7.8594 7.02607 7.7292 7.07813 7.6094C7.1302 7.4896 7.20053 7.3854 7.28907 7.29687C7.3828 7.20313 7.4896 7.1302 7.6094 7.07813C7.7292 7.02607 7.8594 7 8 7ZM14 7C14.1406 7 14.2708 7.02607 14.3906 7.07813C14.5104 7.1302 14.6146 7.20313 14.7031 7.29687C14.7969 7.3854 14.8698 7.4896 14.9219 7.6094C14.9739 7.7292 15 7.8594 15 8C15 8.1406 14.9739 8.2708 14.9219 8.3906C14.8698 8.5104 14.7969 8.6172 14.7031 8.71093C14.6146 8.79947 14.5104 8.8698 14.3906 8.92187C14.2708 8.97393 14.1406 9 14 9C13.8594 9 13.7292 8.97393 13.6094 8.92187C13.4896 8.8698 13.3828 8.79947 13.2891 8.71093C13.2005 8.6172 13.1302 8.5104 13.0781 8.3906C13.0261 8.2708 13 8.1406 13 8C13 7.8594 13.0261 7.7292 13.0781 7.6094C13.1302 7.4896 13.2005 7.3854 13.2891 7.29687C13.3828 7.20313 13.4896 7.1302 13.6094 7.07813C13.7292 7.02607 13.8594 7 14 7Z"></path>
        </svg>
    </fluent-button>
`;

const overflowTemplate = html<NavBar>`
    <div class=${x => (x.isOverflowing ? "overflowContainer" : "overflowContainer hidden")} >
        ${overflowButton}
        <ul 
            class=${x => (x.showMenu ? "overflowMenu" : "hidden")}
            aria-labelledby="${overflowButtonId}"
        >
            ${repeat(x => x.overflowItems, navItemTemplate)}
        </ul>
    </div>
`;

export const navBarTemplate = html<NavBar>`
    <template role="navigation">
        <ul class="list" part="list" ${ref("navContainer")}>
            ${repeat(x => x.data, navItemTemplate)}
        </ul>
        ${overflowTemplate}
        <div class="endContainer" part="endContainer">
            ${endTemplate}
        </div>
    </template>
`;
